// Provider naming rule (design pass D6, 2026-10-12).
//
// The doctor title ("Dr.", bg "Д-р", it "Dott.", el "Δρ.") belongs only to
// medical doctors and dentists. Psychologists, physiotherapists,
// nutritionists, midwives, occupational and speech therapists, osteopaths,
// optometrists and audiologists are shown by their bare name — a title they
// don't hold would misstate their credentials on a medical site.
//
// `profession` mirrors `Provider.profession` (schema default `md_doctor`).
// A payload that doesn't carry it (`undefined` / `null` / "") falls back to
// that default, so doctors keep their title while payloads catch up.

/** Professions that carry the doctor title. */
export const DOCTOR_TITLE_PROFESSIONS = ["md_doctor", "dentist"] as const;

/** True when the provider is shown with the doctor title. */
export function hasDoctorTitle(profession?: string | null): boolean {
  if (profession == null || profession === "") return true;
  return (DOCTOR_TITLE_PROFESSIONS as readonly string[]).includes(profession);
}

/** Localized doctor title per UI language. Unlisted locales use "Dr.". */
const DOCTOR_TITLE_BY_LOCALE: Readonly<Record<string, string>> = {
  ro: "Dr.",
  en: "Dr.",
  bg: "Д-р",
  de: "Dr.",
  fr: "Dr",
  hu: "Dr.",
  it: "Dott.",
  el: "Δρ.",
};

/** The doctor title for a UI locale ("ro", "bg-BG", …). */
export function doctorTitle(locale?: string | null): string {
  const base = (locale ?? "").toLowerCase().split(/[-_]/)[0] ?? "";
  return DOCTOR_TITLE_BY_LOCALE[base] ?? "Dr.";
}

/** A title already baked into a stored name ("Dr. Ana Pop", "Д-р …"). */
const EXISTING_TITLE_RE = /^(?:dr\.?|д-р|dott\.(?:ssa)?|dra\.|δρ\.)\s+/i;

/** The name without a leading doctor title. */
export function stripDoctorTitle(name: string): string {
  return name.trim().replace(EXISTING_TITLE_RE, "").trim();
}

/**
 * Display name for a provider: the localized doctor title for doctors and
 * dentists, the bare name for every other profession. Strips a title that
 * is already part of `name`, so it never doubles up.
 */
export function providerDisplayName(
  name: string | null | undefined,
  opts: { profession?: string | null; locale?: string | null } = {},
): string {
  const bare = stripDoctorTitle(name ?? "");
  if (!bare) return "";
  return hasDoctorTitle(opts.profession) ? `${doctorTitle(opts.locale)} ${bare}` : bare;
}

/** `providerDisplayName` for a `{ firstName, lastName, profession }` record. */
export function providerFullDisplayName(
  provider: {
    firstName?: string | null;
    lastName?: string | null;
    profession?: string | null;
  },
  locale?: string | null,
): string {
  const name = [provider.firstName, provider.lastName]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return providerDisplayName(name, { profession: provider.profession, locale });
}
